import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMotorcycle,
  faTaxi,
  faTruckFast,
  faTruckFront,
  faTruckPickup,
} from "@fortawesome/free-solid-svg-icons";

const icons = [
  faMotorcycle,
  faTaxi,
  faTruckFast,
  faTruckFront,
  faTruckPickup,
];

// -----------------------------------
// Animated Vehicle Icon
// -----------------------------------
const AnimatedVehicleIcon = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % icons.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex justify-center mb-6">
      <div className="relative w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center overflow-hidden">
        {icons.map((icon, index) => (
          <FontAwesomeIcon
            key={index}
            icon={icon}
            className={`absolute text-white text-2xl transition-opacity duration-700 ${
              index === activeIndex ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// -----------------------------------
// OTP Component
// -----------------------------------
const OTPVerification = ({ mobile }) => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const VerifyOtpApi =
    process.env.REACT_APP_FORE_BRICKBUCKET +
    "delivery-partner/verify-otp-and-login";

  const isOtpValid = otp.every((d) => d !== "");

  const handleVerifyOtp = async () => {
    if (!isOtpValid) return;

    try {
      setLoading(true);
      setError("");

      const res = await fetch(VerifyOtpApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mobile,
          otp: Number(otp.join("")),
        }),
      });

      const data = await res.json();
      console.log(data);
      if (!res.ok) {
        setError(data.message || "Invalid OTP");
        return;
      }

      // ✅ Save token/session
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("deliveryPartner", JSON.stringify(data));

      // ✅ Navigate ONLY after successful OTP verification
    navigate(
  `/delivery/rides?mobile=${encodeURIComponent(mobile)}`,
  { state: { isBrickbucket: true } }
);
    } catch (err) {
      setError("OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  return (
    <>
      <h2 className="text-sm font-semibold text-gray-900 mb-4">Enter OTP</h2>

     <div className="flex justify-between mb-4">
  {otp.map((digit, index) => (
    <input
      key={index}
      id={`otp-${index}`}
      maxLength={1}
      value={digit}
      onChange={(e) => handleChange(e.target.value, index)}
      className="w-14 h-14 text-center text-xl rounded-xl bg-white 
                 border border-[#C20C0C] text-gray-800
                 focus:outline-none focus:ring-0 focus:border-red-500"
    />
  ))}
</div>

      {error && (
        <p className="text-red-500 text-sm mb-3 text-center">{error}</p>
      )}

      <button
        disabled={!isOtpValid || loading}
        onClick={handleVerifyOtp}
        className={`w-full py-4 rounded-xl font-bold ${
          isOtpValid
            ? "bg-red-600 text-white"
            : "bg-gray-200 text-gray-400"
        }`}
      >
        {loading ? "Verifying..." : "Verify & Login"}
      </button>
    </>
  );
};


// -----------------------------------
// Main Component
// -----------------------------------
const PartnerLogin = () => {
  const [mobile, setMobile] = useState("");
  const [step, setStep] = useState("MOBILE");
  const [error, setError] = useState("");

  const LoginApi =
    process.env.REACT_APP_FORE_BRICKBUCKET +
    "delivery-partner/delivery-partner-login";

  const isValidMobile = mobile.length === 10;

  const handleLogin = async () => {
    try {
      setError("");

      const res = await fetch(LoginApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_number: mobile }),
      });

      const data = await res.json();
      console.log(data);
      if (!res.ok) {
        setError(data.message || "Login failed");
        return;
      }

      // ✅ Move to OTP screen ONLY after OTP sent
      setStep("OTP");
    } catch (err) {
      setError("Server error. Try again.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm border border-[#C20C0C] rounded-2xl p-8">

        <AnimatedVehicleIcon />

        {step === "MOBILE" && (
          <>
            <h1 className="text-xl font-semibold text-black text-center mb-4">
              Delivery Partner Login
            </h1>

            <input
              type="tel"
              value={mobile}
              onChange={(e) =>
                setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              placeholder="Enter mobile number"
              className="w-full px-4 py-3 mb-4 rounded-xl text-black border border-[#C20C0C]"
            />

            {error && (
              <p className="text-red-500 text-sm mb-3 text-center">{error}</p>
            )}

            <button
              disabled={!isValidMobile}
              onClick={handleLogin}
              className={`w-full py-4 rounded-xl font-bold ${
                isValidMobile
                  ? "bg-red-600 text-white"
                  : "bg-red-600 text-black"
              }`}
            >
              Get OTP
            </button>
          </>
        )}

        {step === "OTP" && <OTPVerification mobile={mobile} />}
      </div>
    </div>
  );
};

export default PartnerLogin;
